import type { PriceFacts, WireListing, WirePriceEstimate } from "../types";

import { createEmptyPriceFacts } from "./createEmptyPriceFacts";
import { positiveOrNull } from "./facts.utils";
import { getFoodDrinkType } from "./foodDrinkType.utils";

export type FromPriceEstimateParams = {
  estimate: WirePriceEstimate | null | undefined;
  listing: WireListing | null | undefined;
  /** Booking start (ISO), for the Due-at-venue row's date caption. */
  bookingStart?: string | null;
  /** Event end (ISO), fallback for the Due-at-venue row's date caption. */
  eventEnd?: string | null;
};

/**
 * Reads a listing's minimum-duration estimate into `PriceFacts`. Although no
 * event times are selected, the estimate represents a real minimum-duration
 * subtotal and therefore uses the dated price-summary rows.
 */
export const fromPriceEstimate = ({
  estimate,
  listing,
  bookingStart = null,
  eventEnd = null,
}: FromPriceEstimateParams): PriceFacts => {
  const listingIsFlatRate = (
    listing as { is_flat_rate?: boolean | null } | null | undefined
  )?.is_flat_rate;

  const isDiy =
    estimate?.is_flat_rate != null
      ? !estimate.is_flat_rate
      : listingIsFlatRate != null
        ? !listingIsFlatRate
        : listing?.full_service?.active !== true;
  const listingCountry = listing?.country ?? null;
  const isDepositListing =
    listing?.pay_at_venue?.type === "minimum_spend_deposit";
  const commonFacts = createEmptyPriceFacts({
    isDiy,
    datesKnown: true,
    roomType: listing?.full_service?.room_type ?? null,
    foodDrinkType: getFoodDrinkType({
      drinkOption: listing?.full_service?.drink_options,
      foodPackage: listing?.full_service?.food_packages?.[0],
    }),
    isDepositListing,
    listingCountry,
    isUsListing: listingCountry === "US",
    bookingStart,
    eventEnd,
  });

  if (!estimate) {
    return commonFacts;
  }

  return {
    ...commonFacts,
    hourlyRate: estimate.booking_rate ?? null,
    spaceRental: isDiy
      ? (estimate.min_duration_price ?? null)
      : (estimate.flat_rate_booking_subtotal ?? null),
    durationHours: isDiy
      ? (estimate.min_duration_hours ?? null)
      : (listing?.full_service?.min_booking_duration_hours ?? null),
    dayRateDiscount: positiveOrNull(estimate.discount),
    dayRateDiscountPercent: estimate.discount_percent ?? null,
    cleaningFee: positiveOrNull(estimate.cleaning_fee),
    foodDrinkMinimum: isDiy
      ? null
      : positiveOrNull(estimate.flat_rate_fandb_amount),
    gratuityAmount: isDiy
      ? null
      : positiveOrNull(estimate.flat_rate_gratuity_amount),
    gratuityRate: isDiy
      ? null
      : positiveOrNull(estimate.flat_rate_gratuity_percent),
    processingFee: isDiy
      ? (estimate.min_duration_fees ?? null)
      : (estimate.guest_processing_fee ?? null),
    originalProcessingFee: positiveOrNull(estimate.guest_original_fees?.fee),
    bookingTotal: isDiy
      ? (estimate.min_duration_total ?? null)
      : (estimate.total ?? null),
    // Deposit-type PAV only — the estimate response has no equivalent field for
    // standard (non-deposit) PAV listings, so Section 3 stays hidden there
    // rather than showing a misleading $0 (matches mobile's existing behavior).
    dueToday:
      isDiy || !isDepositListing ? null : (estimate.deposit_amount ?? null),
    dueAtVenue:
      isDiy || !isDepositListing
        ? null
        : positiveOrNull(estimate.pay_at_venue_amount),
  };
};
